import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '../icons/Icon'
import { InlineAlert } from '../InlineAlert'
import { StatusPill } from '../StatusPill'
import { listEvents } from '../../api/events'
import { ApiError } from '../../api/problem'
import type { TimelineEntry } from '../../types/timeline'

export interface TimelineTarget {
  type: string
  id: string
}

export interface TimelinePanelProps {
  target: TimelineTarget
}

const PAGE_LIMIT = 50

/** Every primary-entity page's row-click deep-link target, per subject aggregate type — a subject
 * with no detail page of its own (ProgramFiling, Dossier, CaseTeamMember) renders as plain text,
 * never a dead link (CLAUDE.md §4: "every CTA must open a real page"). `Branch`/`Contact` gained
 * detail pages in the CRM-completeness pass (IB-93) — added here so their own Timeline entries
 * (and any cross-entity Timeline that surfaces them) link out instead of rendering as text. */
function subjectHref(aggregateType: string, aggregateId: string): string | null {
  switch (aggregateType) {
    case 'Account':
      return `/clients/accounts/${aggregateId}`
    case 'LegalEntity':
      return `/clients/legal-entities/${aggregateId}`
    case 'Branch':
      return `/clients/branches/${aggregateId}`
    case 'Contact':
      return `/clients/contacts/${aggregateId}`
    case 'Case':
      return `/cases/${aggregateId}`
    default:
      return null
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return new Date(iso).toLocaleDateString()
}

/** A human-readable verb from `eventType` — e.g. `crm.account.updated.v1` -> "account updated". */
function summarize(eventType: string): string {
  const withoutVersion = eventType.replace(/\.v\d+$/, '')
  const parts = withoutVersion.split('.').slice(1)
  return (parts.length > 0 ? parts : [withoutVersion]).join(' ').replace(/_/g, ' ')
}

/**
 * The universal append-only Timeline feed (docs/design-system.md §3.1, docs/adr-log.md ADR-0046) —
 * ONE shared component every primary-entity page composes (design-system §3.2's "one component,
 * one contract" rule, extended here): `role="feed"`, events rendered as `<article>`s, newest-first,
 * filter by type (server-side)/actor/date (client-side over the loaded pages — the backend contract,
 * `GET /events?target&event_type&cursor`, has no actor/date query param; a disclosed, honest scope
 * limit, not a silent gap), each event deep-links to its subject, "Load older" cursor pagination.
 *
 * True virtualization (windowed DOM rendering within an already-loaded page) is NOT implemented —
 * the same disclosed gap `design-system.md`'s `ListView`/`DataGrid` already carries platform-wide
 * (no virtualization library exists anywhere in this codebase yet); cursor pagination bounds how
 * much of a long history is ever fetched/rendered at once.
 */
export function TimelinePanel({ target }: TimelinePanelProps) {
  const [entries, setEntries] = useState<TimelineEntry[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [eventTypeFilter, setEventTypeFilter] = useState('')
  const [actorFilter, setActorFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const load = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await listEvents({
          targetType: target.type,
          targetId: target.id,
          eventType: eventTypeFilter || null,
          cursor: afterCursor,
          limit: PAGE_LIMIT,
        })
        setEntries((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load the timeline.')
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [target.type, target.id, eventTypeFilter],
  )

  useEffect(() => {
    void load(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.type, target.id, eventTypeFilter])

  const knownEventTypes = useMemo(
    () => [...new Set(entries.map((e) => e.eventType))].sort(),
    [entries],
  )

  const visibleEntries = useMemo(() => {
    const fromMs = fromDate ? new Date(fromDate).getTime() : null
    // Inclusive of the whole "to" day.
    const toMs = toDate ? new Date(toDate).getTime() + 24 * 60 * 60 * 1000 : null
    const actorNeedle = actorFilter.trim().toLowerCase()
    return entries.filter((e) => {
      if (actorNeedle && !(e.actorId ?? '').toLowerCase().includes(actorNeedle)) return false
      const occurredMs = new Date(e.occurredAt).getTime()
      if (fromMs !== null && occurredMs < fromMs) return false
      if (toMs !== null && occurredMs >= toMs) return false
      return true
    })
  }, [entries, actorFilter, fromDate, toDate])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--iw-space-3)', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--iw-size-xs)' }}>
          Event type
          <select
            className="iw-input"
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            aria-label="Filter by event type"
          >
            <option value="">All types</option>
            {knownEventTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--iw-size-xs)' }}>
          Actor
          <input
            className="iw-input"
            type="text"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            placeholder="Filter by actor id"
            aria-label="Filter by actor"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--iw-size-xs)' }}>
          From
          <input className="iw-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--iw-size-xs)' }}>
          To
          <input className="iw-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        </label>
      </div>

      {error ? (
        <InlineAlert severity="error" action={<button type="button" className="iw-btn iw-btn--sm" onClick={() => void load(null, true)}>Try again</button>}>
          {error}
        </InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading timeline…</p>
      ) : visibleEntries.length === 0 ? (
        <InlineAlert severity="info">Nothing on the timeline yet.</InlineAlert>
      ) : (
        <ul
          role="feed"
          aria-busy={loadingMore}
          aria-label="Timeline"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}
        >
          {visibleEntries.map((entry, index) => {
            const href = subjectHref(entry.aggregateType, entry.aggregateId)
            return (
              <li key={entry.eventId}>
                <article
                  aria-posinset={index + 1}
                  aria-setsize={-1}
                  style={{
                    display: 'flex',
                    gap: 'var(--iw-space-3)',
                    padding: 'var(--iw-space-3)',
                    background: 'var(--iw-surface)',
                    border: '1px solid var(--iw-border)',
                    borderRadius: 'var(--iw-radius-md)',
                  }}
                >
                  <Icon name="activity" size={16} style={{ color: 'var(--iw-accent)', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
                      <span style={{ fontWeight: 'var(--iw-weight-medium)' as never }}>{summarize(entry.eventType)}</span>
                      {entry.channel ? <StatusPill variant="neutral" label={entry.channel} /> : null}
                      {href ? (
                        <Link to={href} style={{ color: 'var(--iw-accent)', fontSize: 'var(--iw-size-xs)' }}>
                          View {entry.aggregateType}
                        </Link>
                      ) : (
                        <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>{entry.aggregateType}</span>
                      )}
                    </div>
                    {entry.reason ? <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>{entry.reason}</p> : null}
                    <div style={{ display: 'flex', gap: 'var(--iw-space-2)', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>
                      {entry.actorId ? <span title={entry.actorId}>Actor {entry.actorId.slice(0, 8)}</span> : <span>System</span>}
                      <time dateTime={entry.occurredAt} title={new Date(entry.occurredAt).toLocaleString()}>
                        {relativeTime(entry.occurredAt)}
                      </time>
                    </div>
                  </div>
                </article>
              </li>
            )
          })}
        </ul>
      )}

      {!loading && !error && cursor ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" className="iw-btn" onClick={() => void load(cursor, false)} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load older'}
          </button>
        </div>
      ) : null}
    </div>
  )
}
